import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { useParams, useHistory, useLocation } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Stack,
  Tooltip,
  Skeleton,
  Breadcrumbs,
  Link as MuiLink,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  ShareOutlined as ShareIcon,
  NavigateNext as NavigateNextIcon,
} from "@mui/icons-material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Web3 & Services
import cinstance from "../../../Service/randomcinstance";
import usercinstance from "../../../Service/usercinstance";
import web3 from "../../../Service/web3";
import { colors, borderRadiusPx } from "../../../../theme";
import { Path } from "../../../../static/Constants";

// Dashboard Layout & Shared Components
import { DashboardLayout } from "../layout";
import { CommonButton, CommonChip } from "../../ui";

// Domain Lottery Components
import { LOTTERY_TYPES, STATUS_CONFIG } from "./AdminLotteryCard";
import LotteryDetailsCard from "./LotteryDetailsCard";
import LotteryDetailTicketsTable from "./LotteryDetailTicketsTable";

/**
 * Unified LotteryDetailsPage Component
 * Supports both Admin and Creator views dynamically based on role.
 */
const LotteryDetailsPage = ({ role }) => {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();

  // Determine active role: 'admin' | 'creator'
  const resolvedRole =
    role || (location.pathname.startsWith("/admin") ? "admin" : "creator");
  const isAdmin = resolvedRole === "admin";

  // Authentication & Access
  const [account, setAccount] = useState("");
  const [isAuthorized, setIsAuthorized] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const [creatorInfo, setCreatorInfo] = useState({
    name: "Creator",
    active: true,
  });

  // Data State
  const [lottery, setLottery] = useState(null);
  const [datelist, setDatelist] = useState(null);
  const [lotterylist, setLotterylist] = useState([]);
  const [winningNumbers, setWinningNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  /**
   * Fetch all lottery details, dates, participants and winner events from smart contracts
   */
  const fetchLotteryDetails = useCallback(async () => {
    if (!id) return;

    try {
      if (!window.ethereum && !web3) {
        setLoading(false);
        return;
      }

      window.web3 = web3;
      let accounts = [];
      try {
        if (window.ethereum?.request) {
          accounts = await window.ethereum.request({ method: "eth_accounts" });
        }
        if (!accounts || accounts.length === 0) {
          accounts = await web3.eth.getAccounts();
        }
      } catch (accErr) {
        console.warn("Could not retrieve accounts", accErr);
      }

      const userAccount = accounts && accounts.length > 0 ? accounts[0] : "";
      setAccount(userAccount);

      if (isAdmin) {
        // Admin Access Verification
        let contractAdmin = "";
        try {
          contractAdmin = await cinstance.methods.admin().call();
        } catch (adminErr) {
          console.warn("Could not get contract admin", adminErr);
        }

        const authorized = Boolean(
          contractAdmin &&
            userAccount &&
            contractAdmin.toLowerCase() === userAccount.toLowerCase(),
        );
        setIsAuthorized(authorized);
        setAuthChecked(true);

        if (!authorized) {
          setLoading(false);
          return;
        }
      } else {
        // Creator Access Verification
        try {
          const orgData = await usercinstance.methods
            .organisationbyaddr(userAccount)
            .call();
          setCreatorInfo(orgData || { name: "Creator", active: true });
          setIsAuthorized(Boolean(orgData ? orgData.active : true));
        } catch (orgErr) {
          console.warn("Could not fetch creator org info", orgErr);
          setCreatorInfo({ name: "Creator", active: true });
          setIsAuthorized(true);
        }
        setAuthChecked(true);
      }

      // 1. Fetch main lottery parameters
      const lotteryResponse = await cinstance.methods.lottery(id).call();
      setLottery({
        ...lotteryResponse,
        lotteryId: id,
      });

      // 2. Fetch lottery schedule / dates
      try {
        const datesResponse = await cinstance.methods.lotteryDates(id).call();
        setDatelist(datesResponse);
      } catch (dateErr) {
        console.warn("Could not fetch lotteryDates", dateErr);
      }

      // 3. Fetch participants and tickets bought
      const parsedList = [];
      let numbersResponse = null;
      try {
        numbersResponse = await cinstance.methods.getLotteryNumbers(id).call();
        if (numbersResponse) {
          const userAddresses =
            numbersResponse.useraddress || numbersResponse[1] || [];
          const rawTickets =
            numbersResponse.tickets || numbersResponse[0] || [];
          const isMissile = String(lotteryResponse.lotteryType) === "3";
          const pickCount = Number(lotteryResponse.pickNumbers || 1);

          userAddresses.forEach((userAddr, index) => {
            if (!isMissile) {
              const ticketSlice = rawTickets.slice(
                index * pickCount,
                (index + 1) * pickCount,
              );
              parsedList.push({
                id: `${userAddr}-${index}`,
                useraddress: userAddr,
                ticketbought: ticketSlice || [],
              });
            } else {
              parsedList.push({
                id: `${userAddr}-${index}`,
                useraddress: userAddr,
                ticketbought: rawTickets[index] || "",
              });
            }
          });
        }
        setLotterylist(parsedList);
      } catch (numErr) {
        console.warn("Could not fetch getLotteryNumbers", numErr);
      }

      // 4. Fetch winning numbers from events and contract methods
      let winNum = [];
      try {
        let events = [];
        try {
          events = await cinstance.getPastEvents("LotteryResult", {
            filter: { lotteryId: [id, `${id}`, Number(id)] },
            fromBlock: 0,
            toBlock: "latest",
          });
        } catch (filterErr) {
          try {
            const allEvts = await cinstance.getPastEvents("LotteryResult", {
              fromBlock: 0,
              toBlock: "latest",
            });
            events = allEvts.filter(
              (e) => String(e?.returnValues?.lotteryId) === String(id),
            );
          } catch (allErr) {
            console.warn("Could not fetch all LotteryResult events", allErr);
          }
        }

        if (events && events.length > 0) {
          const eventData = events[events.length - 1]?.returnValues;
          if (eventData?.n) {
            winNum = Array.isArray(eventData.n) ? eventData.n : [eventData.n];
          } else if (
            eventData?.number !== undefined &&
            eventData?.number !== null
          ) {
            const numStr = String(eventData.number);
            if (numStr.includes("-")) {
              winNum = numStr.split("-").filter(Boolean);
            } else if (numStr.trim().length > 0) {
              winNum = [numStr.trim()];
            }
          } else if (
            eventData?.winnerNum !== undefined &&
            eventData?.winnerNum !== null
          ) {
            winNum = [String(eventData.winnerNum)];
          }
        }
      } catch (evtErr) {
        console.warn("Could not fetch LotteryResult events", evtErr);
      }

      // Fallback A: SpinLotteryResult for spin / missile / spinner lotteries
      if (!winNum || winNum.length === 0) {
        try {
          let spinEvents = [];
          try {
            spinEvents = await cinstance.getPastEvents("SpinLotteryResult", {
              filter: { lotteryId: [id, `${id}`, Number(id)] },
              fromBlock: 0,
              toBlock: "latest",
            });
          } catch (sErr) {
            try {
              const allSpin = await cinstance.getPastEvents(
                "SpinLotteryResult",
                {
                  fromBlock: 0,
                  toBlock: "latest",
                },
              );
              spinEvents = allSpin.filter(
                (e) => String(e?.returnValues?.lotteryId) === String(id),
              );
            } catch (sAllErr) {
              console.warn(
                "Could not fetch all SpinLotteryResult events",
                sAllErr,
              );
            }
          }

          if (spinEvents && spinEvents.length > 0) {
            const spinData = spinEvents[spinEvents.length - 1]?.returnValues;
            if (
              spinData?.winnerNum !== undefined &&
              spinData?.winnerNum !== null
            ) {
              winNum = [String(spinData.winnerNum)];
            } else if (
              spinData?.selectedNum !== undefined &&
              spinData?.selectedNum !== null
            ) {
              winNum = [String(spinData.selectedNum)];
            } else if (
              spinData?.number !== undefined &&
              spinData?.number !== null
            ) {
              const numStr = String(spinData.number);
              winNum = numStr.includes("-")
                ? numStr.split("-").filter(Boolean)
                : [numStr.trim()];
            }
          }
        } catch (spinEvtErr) {
          console.warn("Could not fetch SpinLotteryResult events", spinEvtErr);
        }
      }

      // Fallback B: getMineLotteryNumbers for Mine lotteries
      if (
        (!winNum || winNum.length === 0) &&
        String(lotteryResponse?.lotteryType) === "1"
      ) {
        try {
          const mineNumbers = await cinstance.methods
            .getMineLotteryNumbers(id)
            .call();
          if (mineNumbers && mineNumbers.length > 0) {
            winNum = mineNumbers.map((n) => String(n));
          }
        } catch (mineErr) {
          console.warn("Could not fetch getMineLotteryNumbers", mineErr);
        }
      }

      // Fallback C: If winner is assigned in smart contract, lookup winner's tickets
      const winnerAddr = (lotteryResponse?.lotteryWinner || "").toLowerCase();
      const isWinnerValid =
        winnerAddr &&
        winnerAddr !== "0x0000000000000000000000000000000000000000" &&
        winnerAddr !== "0x0";

      if (
        (!winNum || winNum.length === 0) &&
        isWinnerValid &&
        parsedList.length > 0
      ) {
        const winnerEntries = parsedList.filter(
          (item) => (item.useraddress || "").toLowerCase() === winnerAddr,
        );
        if (winnerEntries.length > 0) {
          const ticketsFound = [];
          winnerEntries.forEach((entry) => {
            if (Array.isArray(entry.ticketbought)) {
              entry.ticketbought.forEach((t) => ticketsFound.push(String(t)));
            } else if (
              typeof entry.ticketbought === "string" &&
              entry.ticketbought.trim()
            ) {
              if (entry.ticketbought.includes("-")) {
                entry.ticketbought
                  .split("-")
                  .filter(Boolean)
                  .forEach((t) => ticketsFound.push(t));
              } else {
                ticketsFound.push(entry.ticketbought.trim());
              }
            } else if (typeof entry.ticketbought === "number") {
              ticketsFound.push(String(entry.ticketbought));
            }
          });
          if (ticketsFound.length > 0) {
            winNum = ticketsFound;
          }
        }
      }

      setWinningNumbers(winNum || []);
    } catch (error) {
      console.error("Error fetching lottery details:", error);
      toast.error(`Failed to load details for Lottery #${id}`);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [id, isAdmin]);

  useEffect(() => {
    fetchLotteryDetails();

    if (window.ethereum?.on) {
      const handleAccountsChanged = (newAccounts) => {
        if (newAccounts && newAccounts.length > 0) {
          setAccount(newAccounts[0]);
        }
        fetchLotteryDetails();
      };

      const handleChainChanged = () => {
        fetchLotteryDetails();
      };

      window.ethereum.on("accountsChanged", handleAccountsChanged);
      window.ethereum.on("chainChanged", handleChainChanged);

      return () => {
        if (window.ethereum?.removeListener) {
          window.ethereum.removeListener(
            "accountsChanged",
            handleAccountsChanged,
          );
          window.ethereum.removeListener("chainChanged", handleChainChanged);
        }
      };
    }
  }, [fetchLotteryDetails]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchLotteryDetails();
    toast.success("Lottery details refreshed from smart contract!");
  };

  const handleShare = () => {
    const playPath = Path.getLotteryPlayUrl(id, lottery?.lotteryType);
    const url = `${window.location.origin}${playPath}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url);
      toast.success(`Shareable player link for Lottery #${id} copied!`);
    }
  };

  const typeConfig = LOTTERY_TYPES[lottery?.lotteryType] || LOTTERY_TYPES[0];
  const statusObj = STATUS_CONFIG[lottery?.status] || STATUS_CONFIG[0];
  const listRoute = isAdmin ? Path.adminlotterylist : Path.orgmylottery;
  const dashboardRoute = isAdmin ? Path.admin : Path.org;

  return (
    <DashboardLayout
      role={resolvedRole}
      creatorAddress={account}
      userName={isAdmin ? "AutoBET Admin" : creatorInfo?.name || "Creator"}
      userRole={isAdmin ? "Administrator" : "Creator"}
      authChecked={authChecked}
      isAuthorized={isAuthorized}
      headerType="none"
      accessDeniedProps={{
        title: isAdmin ? "Admin Access Required" : "Account Access Denied",
        message: isAdmin
          ? "This lottery management interface is restricted to platform administrators. Please connect an authorized administrator wallet."
          : "You must be a registered Creator or organization to view Lottery details.",
        actionText: isAdmin ? "Return to Home" : "Register as Organisation",
        actionPath: isAdmin ? Path.root : Path.registerasorg,
      }}
    >
      <ToastContainer position="top-right" autoClose={3000} theme="colored" />

      {/* Breadcrumb Navigation */}
      <Breadcrumbs
        separator={
          <NavigateNextIcon
            fontSize="small"
            sx={{ color: colors.slate[400] }}
          />
        }
        sx={{ mb: 2.5 }}
      >
        <MuiLink
          component="button"
          underline="hover"
          onClick={() => history.push(dashboardRoute)}
          sx={{
            color: colors.slate[500],
            fontSize: "13px",
            fontWeight: 600,
            cursor: "pointer",
            border: "none",
            background: "none",
            p: 0,
            "&:hover": { color: colors.primary.main },
          }}
        >
          {isAdmin ? "Admin Dashboard" : "Creator Dashboard"}
        </MuiLink>
        <MuiLink
          component="button"
          underline="hover"
          onClick={() => history.push(listRoute)}
          sx={{
            color: colors.slate[500],
            fontSize: "13px",
            fontWeight: 600,
            cursor: "pointer",
            border: "none",
            background: "none",
            p: 0,
            "&:hover": { color: colors.primary.main },
          }}
        >
          {isAdmin ? "Lotteries Management" : "My Lotteries"}
        </MuiLink>
        <Typography
          sx={{
            color: colors.slate[900],
            fontSize: "13px",
            fontWeight: 700,
          }}
        >
          Lottery #{id}
        </Typography>
      </Breadcrumbs>

      {/* Header Action Bar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 3.5,
        }}
      >
        <Box>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ mb: 1, flexWrap: "wrap", gap: 1 }}
          >
            <CommonButton
              variant="outlined"
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={() => history.push(listRoute)}
            >
              Back
            </CommonButton>

            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                color: colors.text.title,
                letterSpacing: "-0.5px",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                fontSize: { xs: "1.5rem", sm: "1.85rem" },
              }}
            >
              Lottery #{id}
            </Typography>

            {!loading && (
              <>
                <CommonChip
                  label={typeConfig.name}
                  variant="default"
                  size="small"
                  sx={{
                    backgroundColor: typeConfig.bgColor,
                    color: typeConfig.color,
                    fontWeight: 700,
                    border: `1px solid ${typeConfig.color}30`,
                  }}
                />
                <CommonChip
                  label={statusObj.label}
                  variant={statusObj.variant}
                  size="small"
                />
              </>
            )}
          </Stack>

          <Typography sx={{ color: colors.text.secondary, fontSize: "14px" }}>
            Inspect smart contract state, verified participants, schedule, and
            winner outcome.
          </Typography>
        </Box>

        {/* Quick Actions */}
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Tooltip title="Copy Shareable Player Link" arrow>
            <CommonButton
              variant="action"
              size="small"
              startIcon={<ShareIcon sx={{ fontSize: 18 }} />}
              onClick={handleShare}
            >
              Share
            </CommonButton>
          </Tooltip>

          <Tooltip title="Refresh data from blockchain" arrow>
            <CommonButton
              variant="refresh"
              size="small"
              onClick={handleRefresh}
              loading={refreshing}
            />
          </Tooltip>
        </Stack>
      </Box>

      {/* Loading Skeleton */}
      {loading ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Skeleton
            variant="rectangular"
            height={360}
            sx={{ borderRadius: borderRadiusPx.xl }}
          />
          <Skeleton
            variant="rectangular"
            height={300}
            sx={{ borderRadius: borderRadiusPx.xl }}
          />
        </Box>
      ) : (
        <Stack spacing={3.5}>
          {/* Section 1: Unified Lottery Details Card (All Parameters + Winner Info) */}
          <LotteryDetailsCard
            lottery={lottery}
            datelist={datelist}
            winningNumbers={winningNumbers}
          />

          {/* Section 2: Bought Tickets Table (Bought By & Numbers) */}
          <LotteryDetailTicketsTable
            lotterylist={lotterylist}
            lotteryType={lottery?.lotteryType}
            winningNumbers={winningNumbers}
          />
        </Stack>
      )}
    </DashboardLayout>
  );
};

LotteryDetailsPage.propTypes = {
  role: PropTypes.oneOf(["admin", "creator"]),
};

export default LotteryDetailsPage;
